import { useState, type FormEvent } from 'react'
import s from './Login.module.css'

/* 아이콘은 랜딩 기능 카드와 같은 PNG 에셋을 쓴다 */
type Feat = { icon: string; title: string; lines: [string, string] }

const FEATS: Feat[] = [
  {
    icon: 'ico-bot',
    title: 'AI 업무 지원',
    lines: ['챗봇이 업무를', '더 똑똑하게 도와드려요.'],
  },
  {
    icon: 'ico-bars',
    title: '데이터 인사이트',
    lines: ['데이터를 분석하고', '인사이트를 제공합니다.'],
  },
  {
    icon: 'ico-shield',
    title: '안전한 보안',
    lines: ['기업 데이터는 안전하게', '보호됩니다.'],
  },
]

export default function Login() {
  const [showPw, setShowPw] = useState(false)

  // ponytail: 목업 단계라 제출은 콘솔까지만. 인증 API 붙으면 여기서 POST.
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    console.log('login', Object.fromEntries(form))
  }

  return (
    <div className={s.page}>
      <main className={s.card}>
        <div className={s.brand}>
          <div className={s.name}>
            <em>AI</em> 오토팀
          </div>
          <p className={s.desc}>
            데이터 기반 인사이트와 자동화로
            <br />
            1인 사업의 모든 업무를 더 쉽고 빠르게.
          </p>

          <div className={s.orbit} role="presentation" />

          <div className={s.feats}>
            {FEATS.map((f) => (
              <div key={f.title} className={s.feat}>
                <img className={s.t} src={`/assets/${f.icon}.png`} alt="" />
                <div>
                  <h4>{f.title}</h4>
                  <p>
                    {f.lines[0]}
                    <br />
                    {f.lines[1]}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className={s.copy}>© {new Date().getFullYear()} pyhc. All rights reserved.</p>
        </div>

        <form className={s.panel} onSubmit={onSubmit}>
          <h1>로그인</h1>
          <p className={s.sub}>계정에 로그인하여 AI 오토팀을 이용해보세요.</p>

          <label htmlFor="email">이메일</label>
          <div className={s.field}>
            <svg
              className={s.ic}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <path d="m4 7 8 5.5L20 7" />
            </svg>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="이메일을 입력하세요"
              autoComplete="email"
              required
            />
          </div>

          <label htmlFor="pw">비밀번호</label>
          <div className={`${s.field} ${s.eye}`}>
            <svg
              className={s.ic}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="4" y="10" width="16" height="10" rx="3" />
              <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
            </svg>
            <input
              id="pw"
              name="password"
              type={showPw ? 'text' : 'password'}
              placeholder="비밀번호를 입력하세요"
              autoComplete="current-password"
              required
            />
            <button
              className={s.toggle}
              type="button"
              aria-label={showPw ? '비밀번호 숨기기' : '비밀번호 표시'}
              onClick={() => setShowPw((v) => !v)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12Z" />
                <circle cx="12" cy="12" r="2.8" />
                {showPw && <path d="m4 20 16-16" />}
              </svg>
            </button>
          </div>

          <div className={s.row}>
            <input id="keep" name="keep" type="checkbox" />
            <label htmlFor="keep">로그인 상태 유지</label>
            <a href="#/find">아이디 찾기</a>
            <i aria-hidden="true">·</i>
            <a href="#/find/pw">비밀번호 찾기</a>
          </div>

          <button className={s.btn} type="submit">
            로그인
          </button>

          <div className={s.or}>또는</div>

          <button className={s.btnGoogle} type="button">
            <svg viewBox="0 0 48 48" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M45 24.5c0-1.6-.1-2.8-.4-4H24v7.3h12c-.2 2-1.5 5-4.4 7l6.7 5.2c4-3.7 6.7-9.1 6.7-15.5z"
              />
              <path
                fill="#34A853"
                d="M24 46c5.8 0 10.7-1.9 14.3-5.2l-6.7-5.2c-1.8 1.3-4.2 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-7 5.4C8.1 41.1 15.4 46 24 46z"
              />
              <path
                fill="#FBBC05"
                d="M11.5 28.7c-.5-1.4-.7-2.9-.7-4.7s.3-3.3.7-4.7l-7-5.4C3.3 17 2.5 20.4 2.5 24s.8 7 2.5 10.1l6.5-5.4z"
              />
              <path
                fill="#EA4335"
                d="M24 10.2c4.1 0 6.9 1.8 8.5 3.3l6.2-6C34.7 4 29.8 2 24 2 15.4 2 8.1 6.9 4.5 13.9l7 5.4C13.3 14 18.2 10.2 24 10.2z"
              />
            </svg>
            Google 계정으로 로그인
          </button>

          <p className={s.foot}>
            아직 계정이 없으신가요?<a href="#/signup">회원가입</a>
          </p>
        </form>
      </main>
    </div>
  )
}
